import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import _ from 'lodash';
import moment from 'moment';
import BillingViewStore from './BillingViewStore';
import pageOf from './pageOf';

// Invoices that exist in QuickBooks, even if the CSV failed to attach.
const createdStatuses = ['Created', 'AttachmentFailed'];
const emptyFilters = { seller: '', amount: '', fromDate: '', toDate: '' };

// The Export to QuickBooks page: pick matched sellers and create their invoices, and look back at past invoices.
class BillingQuickBooksViewStore extends BillingViewStore {
	@observable result = null;
	// Every invoice already created in QuickBooks, newest first.
	@observable.ref sentInvoices = null;
	@observable sentInvoicesPage = 0;
	// Text typed into the sent invoices filters, as is.
	sentInvoiceFilters = observable({ ...emptyFilters });
	// Sellers ticked in the Send to QuickBooks table.
	selectedSellerIds = observable.set();

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.refresh();
	}

	// Sellers that weren't invoiced in the last send, with why; the ones that were are in the sent invoices.
	@computed get notInvoiced() {
		return _.reject(this.result && this.result.sellers, x => _.includes(createdStatuses, x.status));
	}

	@computed get filteredSentInvoices() {
		const { seller, amount, fromDate, toDate } = this.sentInvoiceFilters;
		const sellerText = _.toLower(_.trim(seller));
		// Matches as you type: the amount, written as digits and a decimal point ("14980.68"), contains what was typed,
		// ignoring "$" and commas, so "1,275" and ".6" both narrow the list.
		const amountText = String(amount).replace(/[^0-9.]/g, '');
		return _.filter(
			this.sentInvoices,
			x =>
				(!sellerText || _.includes(_.toLower(x.sellerName), sellerText)) &&
				(!amountText || _.includes(Number(x.amount).toFixed(2), amountText)) &&
				(!fromDate || x.sentDay >= fromDate) &&
				(!toDate || x.sentDay <= toDate)
		);
	}

	@computed get sentInvoicesPageRows() {
		return pageOf(this.filteredSentInvoices, this.sentInvoicesPage);
	}

	@action.bound
	setSentInvoiceFilter(name, value) {
		this.sentInvoiceFilters[name] = value;
		this.sentInvoicesPage = 0;
	}

	@action.bound
	clearSentInvoiceFilters() {
		Object.assign(this.sentInvoiceFilters, emptyFilters);
		this.sentInvoicesPage = 0;
	}

	@action.bound
	setSentInvoicesPage(page) {
		this.sentInvoicesPage = page;
	}

	// Loaded on every refresh, so a send shows up in the history right away.
	async beforeLoadMatches() {
		const response = await this.billingService.findSentInvoices();
		runInAction(() => {
			// The server sends UTC; sentDay (local, YYYY-MM-DD) is what the date filters compare against.
			this.sentInvoices = _.map(response.data, x => {
				const sentAt = moment.utc(x.dateSent).local();
				return { ...x, sentAt, sentDay: sentAt.format('YYYY-MM-DD') };
			});
		});
	}

	// One row per matched seller: what its QuickBooks invoice would be.
	@computed get sellerSummaries() {
		return _.sortBy(
			_.map(_.groupBy(this.matchedPackages, 'sellerId'), packages => {
				const netCharge = _.sumBy(packages, 'netCharge');
				const upcharge = _.sumBy(packages, x => x.upcharge || 0);
				return {
					sellerId: packages[0].sellerId,
					sellerName: packages[0].sellerName,
					packageCount: packages.length,
					packagesWithoutUpchargeCount: _.filter(packages, x => x.chargesWithoutUpchargeCount > 0).length,
					netCharge,
					upcharge,
					total: netCharge + upcharge,
				};
			}),
			x => _.toLower(x.sellerName)
		);
	}

	@computed get selectedSummaries() {
		return _.filter(this.sellerSummaries, x => this.selectedSellerIds.has(x.sellerId));
	}

	@computed get isEverySellerSelected() {
		return this.sellerSummaries.length > 0 && this.selectedSummaries.length === this.sellerSummaries.length;
	}

	@action.bound
	toggleSeller(sellerId) {
		if (this.selectedSellerIds.has(sellerId)) {
			this.selectedSellerIds.delete(sellerId);
		} else {
			this.selectedSellerIds.add(sellerId);
		}
	}

	@action.bound
	toggleEverySeller() {
		if (this.isEverySellerSelected) {
			this.selectedSellerIds.clear();
		} else {
			_.each(this.sellerSummaries, x => this.selectedSellerIds.add(x.sellerId));
		}
	}

	@action.bound
	confirmSendToQuickBooks() {
		const sellers = this.selectedSummaries;
		if (sellers.length === 0) return;
		this.openConfirmationModal({
			message: [
				'BILLING.SENDCONFIRM',
				{
					count: sellers.length,
					amount: this.rootStore.usdFormatter.format(_.sumBy(sellers, 'total')),
					sellers: _.map(sellers, 'sellerName').join(', '),
				},
			],
			onConfirm: async () => await this.sendToQuickBooks(_.map(sellers, 'sellerId')),
		});
	}

	@action.bound
	async sendToQuickBooks(sellerIds) {
		this.result = null;
		await this.updateMatches('BILLING.STEPINVOICE', async () => {
			const response = await this.billingService.createInvoices(sellerIds);
			runInAction(() => {
				this.result = response.data;
				this.selectedSellerIds.clear();
			});
			this.notifySendResult(response.data.sellers);
		});
	}

	notifySendResult(sellers) {
		const notificationStore = this.rootStore.notificationStore;
		const created = _.filter(sellers, x => _.includes(createdStatuses, x.status)).length;
		const notCreated = sellers.length - created;
		if (sellers.length === 0) {
			notificationStore.warning('BILLING.NOTHINGTOBILL');
		}
		if (created > 0) {
			notificationStore.success(['BILLING.SENTSUCCESS', { count: created }]);
		}
		if (notCreated > 0) {
			notificationStore.warning(['BILLING.SENTNOTCREATED', { count: notCreated }]);
		}
	}

	// Drops ticks for sellers that no longer have anything matched, e.g. after someone else sent them.
	onMatchesLoaded() {
		const sellerIds = new Set(_.map(this.sellerSummaries, 'sellerId'));
		_.each(Array.from(this.selectedSellerIds), id => {
			if (!sellerIds.has(id)) this.selectedSellerIds.delete(id);
		});
	}
}

export default BillingQuickBooksViewStore;
